import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { BottomNav } from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";
import { rciApi } from "../api/rci";
import { ouraApi } from "../api/oura";
import { isOuraUIEnabled } from "../utils/features";
import { protocolsApi } from "../api/protocols";
import { coherenceLabel } from "../utils/coherenceLabels";
import { timeWindow } from "../utils/activeProtocol";
import { dayLabel } from "../utils/dailyLabels";
import { DOMAIN_WORD, DOMAIN_META } from "../utils/domainLabels";
import { PATIENT, lintText } from "../utils/insightRubric";
import { Wordmark } from "../components/Wordmark";

// User-local YYYY-MM-DD. Passed to the BEH-metrics endpoint so its
// 7-day windows bucket against the user's calendar day, not UTC.
function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Display-layer labels — single source in utils/coherenceLabels.js.
const STATUS_LABELS = {
  CALIBRATING: coherenceLabel('CALIBRATING'),
  STABLE_COHERENCE: coherenceLabel('STABLE_COHERENCE'),
  STABILIZING: coherenceLabel('STABILIZING'),
  FRAGMENTED: coherenceLabel('FRAGMENTED'),
  REGRESSING: coherenceLabel('REGRESSING'),
  SAFETY_GATED: coherenceLabel('SAFETY_GATED'),
};

// Static domain scaffold \u2014 display id + plain-language label only.
// Every numeric/narrative field (score, 7-day trend, Key Signals,
// summary, insights) is derived from the user's real snapshot, history,
// and wearable data in the component below. Nothing here is invented
// analysis; sections with no real data source render nothing.
//
// Order + tab numbering are derived from DOMAIN_META (the single
// canonical order) rather than hardcoded here, so this can never drift
// from the shared order again.
const DOMAIN_ORDER = DOMAIN_META.map((d) => d.code);
const DOMAIN_DATA = Object.fromEntries(
  DOMAIN_META.map((d, i) => [
    d.code,
    { id: String(i + 1).padStart(2, "0"), code: d.code, label: d.display },
  ])
);

const INSIGHT_STYLES = {
  Signal:  { bg: "#E4DDD0", color: "#9E8456" },
  Pattern: { bg: "#E4DDD0", color: "#1C1814" },
  Context: { bg: "#E4DDD0", color: "#1C1814" },
};

// SVG Trend Chart
function TrendChart({ history }) {
  const W = 340, H = 120;
  const PAD = { top: 14, right: 16, bottom: 28, left: 30 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const scores = history.map(d => d.score);
  const min = Math.max(0, Math.min(...scores) - 8);
  const max = Math.min(100, Math.max(...scores) + 8);

  const x = i => PAD.left + (i / (history.length - 1)) * innerW;
  const y = v => PAD.top + innerH - ((v - min) / (max - min)) * innerH;

  const linePts = history.map((d, i) => `${x(i)},${y(d.score)}`).join(" ");

  const areaPath = [
    `M ${x(0)},${y(history[0].score)}`,
    ...history.map((d, i) => `L ${x(i)},${y(d.score)}`),
    `L ${x(history.length - 1)},${PAD.top + innerH}`,
    `L ${x(0)},${PAD.top + innerH}`,
    "Z",
  ].join(" ");

  const [hovered, setHovered] = useState(null);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={P.primary} stopOpacity="0.12" />
          <stop offset="100%" stopColor={P.primary} stopOpacity="0" />
        </linearGradient>
      </defs>

      {[0, 0.5, 1].map((t, i) => {
        const yv = PAD.top + innerH * (1 - t);
        const val = Math.round(min + t * (max - min));
        return (
          <g key={i}>
            <line x1={PAD.left} y1={yv} x2={PAD.left + innerW} y2={yv}
              stroke={P.border} strokeWidth="1" strokeDasharray="3 4" />
            <text x={PAD.left - 6} y={yv + 4}
              fontSize="9" fill={P.textFaint} textAnchor="end"
              fontFamily="DM Sans, sans-serif">{val}</text>
          </g>
        );
      })}

      <path d={areaPath} fill="url(#areaGrad)" />

      <polyline points={linePts} fill="none"
        stroke={P.primary} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />

      {history.map((d, i) => (
        <g key={i}>
          <text x={x(i)} y={H - 4}
            fontSize="9" fill={hovered === i ? P.primary : P.textFaint}
            textAnchor="middle" fontFamily="DM Sans, sans-serif"
            style={{ transition: "fill 0.15s" }}>
            {d.day}
          </text>

          <rect
            x={x(i) - 14} y={PAD.top}
            width={28} height={innerH}
            fill="transparent"
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          />

          <circle cx={x(i)} cy={y(d.score)} r={hovered === i ? 5 : 3}
            fill={hovered === i ? P.primary : P.surface}
            stroke={P.primary} strokeWidth="2"
            style={{ transition: "r 0.15s" }} />

          {hovered === i && (
            <g>
              <rect
                x={Math.min(Math.max(x(i) - 46, PAD.left), PAD.left + innerW - 92)}
                y={y(d.score) - 38}
                width={92} height={26}
                rx={6} fill={P.dark} />
              <text
                x={Math.min(Math.max(x(i), PAD.left + 46), PAD.left + innerW - 46)}
                y={y(d.score) - 21}
                fontSize="10" fill={P.bg}
                textAnchor="middle" fontFamily="DM Sans, sans-serif" fontWeight="400">
                {d.score} &middot; {d.day}
              </text>
            </g>
          )}
        </g>
      ))}
    </svg>
  );
}

// Score Ring (local variant)
function ScoreRing({ score, size = 86, stroke = 5 }) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={P.border} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={P.primary} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)" }} />
    </svg>
  );
}

// Styles
const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .domain-detail {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    min-height: 100vh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .fu  { animation: fadeUp 0.5s ease forwards; }
  .d1  { animation-delay: 0.05s; opacity: 0; }
  .d2  { animation-delay: 0.16s; opacity: 0; }
  .d3  { animation-delay: 0.27s; opacity: 0; }
  .d4  { animation-delay: 0.38s; opacity: 0; }
  .d5  { animation-delay: 0.50s; opacity: 0; }
  .d6  { animation-delay: 0.62s; opacity: 0; }

  .domain-tab {
    flex: 1; padding: 10px 4px;
    background: none; border: none; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
  }
  .domain-tab.active { border-bottom: 2px solid ${P.primary}; }

  .metric-row {
    display: flex; align-items: flex-start;
    justify-content: space-between;
    padding: 14px 0;
    border-bottom: 1px solid ${P.border};
  }
  .metric-row:last-child { border-bottom: none; }

  .insight-row {
    padding: 16px 0;
    border-bottom: 1px solid ${P.border};
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .insight-row:hover { opacity: 0.72; }
  .insight-row:last-child { border-bottom: none; }

  .pill {
    font-size: 9px; letter-spacing: 0.11em;
    text-transform: uppercase; padding: 3px 9px;
    border-radius: 99px; font-weight: 500;
  }

  .nav-btn {
    flex: 1; background: none; border: none; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    padding: 8px 4px; transition: opacity 0.15s;
  }
  .nav-btn:hover { opacity: 0.65; }
`;

const SCORE_KEY = { BIO: 'bio_score', PSYCH: 'psych_score', BEH: 'beh_score' };

// Weekday label for a snapshot. ``window_end`` is the snapshot's
// local-bucketed DATE (e.g. "2026-05-13") — the canonical day the
// data belongs to. The old code derived the label from ``created_at``
// (a UTC timestamp), which collapsed multiple same-UTC-day snapshots
// to identical weekday labels ("Tue, Tue, Tue") and could shift the
// day across the UTC boundary. Appending ``T00:00:00`` forces local-
// midnight parsing so the date never slips back a day.
function weekdayLabel(windowEnd) {
  if (!windowEnd) return '';
  return new Date(windowEnd + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
}

// Plain-language explanations for the state-machine reason codes that
// accompany every snapshot (GET /snapshot → reason_codes). This is the
// explainable-UX surface those codes exist for — non-clinical, condition-
// focused phrasing only. Codes without an entry are simply not shown.
const REASON_CODE_TEXT = {
  RC_SAFETY_GATED: "Insights are paused while you focus on taking care of yourself.",
  RC_REGRESSION_TREND: "Your signal has eased back from where it was over the past few weeks.",
  RC_ACTIVE_TRIGGER_BLOCKS_STABILITY: "A recent pattern is keeping your signal from settling into a steady rhythm.",
  RC_SEVERE_DOMAIN_CAP: "One area is running low right now, and your overall signal is sitting low alongside it.",
  RC_BOOTSTRAP_GRACE: "You're still early in building your baseline — these first readings carry extra room while Haiven calibrates.",
  RC_STABLE_CRITERIA_MET: "Your signal has held steady and balanced across recent check-ins.",
  RC_STABILIZING_TREND: "Your signal has been trending upward and starting to settle.",
  RC_INSUFFICIENT_CONVERGENCE: "Your areas aren't moving together yet — fewer than two are in a positive range.",
  RC_LOW_DATA: "There aren't enough check-ins yet to read a confident pattern.",
};

// Build the Domain Insights list from the snapshot's real reason codes.
// Returns [] when the snapshot carries no recognised codes, so the
// caller renders nothing rather than fabricated analysis. Every entry
// is gated through the shared patient rubric — a future edit to
// REASON_CODE_TEXT that trips the lint drops that line, not the screen.
// Exported for the rubric-contract tests.
export function buildInsights(reasonCodes, windowEnd) {
  if (!Array.isArray(reasonCodes)) return [];
  const time = weekdayLabel(windowEnd);
  return reasonCodes
    .map((rc) => REASON_CODE_TEXT[rc])
    .filter(Boolean)
    .filter((text) => lintText(text, PATIENT).length === 0)
    .map((text) => ({ type: "Signal", text, time }));
}

// Factual one-line trend summary. The dial next to it already shows the
// number, so the line never restates the score (that's a rubric
// SCORE_RESTATEMENT failure) — it carries only the 7-day movement, the
// one fact the dial can't show. Returns null when there's no trend to
// report. Exported for the rubric-contract tests.
export function buildSummary(domainWord, score, history) {
  if (score == null) return null;
  if (!history || history.length < 2) return null;
  const delta = history[history.length - 1].score - history[0].score;
  const pts = Math.abs(delta) === 1 ? "point" : "points";
  let line;
  if (delta > 0) line = `${domainWord} has moved up ${delta} ${pts} over the last 7 days.`;
  else if (delta < 0) line = `${domainWord} has moved down ${Math.abs(delta)} ${pts} over the last 7 days.`;
  else line = `${domainWord} has held level over the last 7 days.`;
  return lintText(line, PATIENT).length === 0 ? line : null;
}

// Main Component
export default function DomainDetail({ domainCode = "BIO" }) {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeCode, setActiveCode] = useState(domainCode);
  const [snapshot, setSnapshot] = useState(null);
  const [historyRaw, setHistoryRaw] = useState([]);
  const [ouraData, setOuraData] = useState(null);
  const [behMetrics, setBehMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      rciApi.snapshot(user.id, token).catch(() => null),
      rciApi.history(user.id, 14, token).catch(() => []),
      ouraApi.today(token).catch(() => null),
      protocolsApi.behMetrics(localDateKey(), token).catch(() => null),
    ]).then(([snap, hist, oura, beh]) => {
      setSnapshot(snap);
      setHistoryRaw(hist || []);
      if (oura?.synced) setOuraData(oura);
      if (beh?.metrics?.length) setBehMetrics(beh.metrics);
    }).finally(() => setLoading(false));
  }, [user, token]);

  const meta = DOMAIN_DATA[activeCode];
  const domainWord = DOMAIN_WORD[activeCode] || activeCode;

  // Build the view model entirely from real data. Every field is either
  // derived from the user's snapshot / history / wearable readings or
  // left empty so its section renders nothing — never a mock placeholder.
  const D = (() => {
    // A null domain score means "no data for this domain that day" (server
    // scoring_version 2.5+), not a score of 0. It must never render as 1.
    const rawScore = snapshot ? snapshot[SCORE_KEY[activeCode]] : null;
    const score = rawScore != null
      ? Math.max(1, Math.round(rawScore * 100))
      : null;
    const noDataToday = snapshot != null && rawScore == null;

    // ``dayLabel`` derives a per-day phrase from the numeric history
    // (deltas, streaks, outliers, cross-domain gaps). With no real
    // history the trend chart and check-in log render nothing rather
    // than a fabricated week.
    // No-data days are left out of the trend rather than drawn as 1.
    const recent = snapshot
      ? [...historyRaw].reverse().slice(-7).filter(s => s[SCORE_KEY[activeCode]] != null)
      : [];
    const history = recent.length > 0
      ? recent.map((s, idx) => ({
          day: weekdayLabel(s.window_end),
          score: Math.max(1, Math.round(s[SCORE_KEY[activeCode]] * 100)),
          note: dayLabel(idx, recent, activeCode),
        }))
      : [];

    // Key Signals are shown only when a real per-domain source exists:
    //  • BIO  — live Oura readings (when synced)
    //  • BEH  — Key Signals computed from protocol_completions
    // PSYCH carries no per-indicator source (the compute payload sends a
    // single synthetic indicator equal to the domain score), so its Key
    // Signals card renders nothing rather than invented metrics.
    let metrics = null;
    if (activeCode === 'BIO' && ouraData) {
      // Source name reads "your wearable" in the native iOS build while
      // VITE_IOS_OURA_ENABLED is off (utils/features.js).
      const src = isOuraUIEnabled() ? "Oura" : "your wearable";
      metrics = [
        // Higher HRV = good — opposite direction from RHR below (was
        // also existence-only). Same constraint as RHR: no rolling
        // mean available here to delta against, so this reuses
        // CheckIn.jsx's existing HRV band ("Under load" below 30ms,
        // good otherwise) rather than inventing a new threshold.
        { label: "HRV",        value: ouraData.hrv != null ? `${Math.round(ouraData.hrv)} ms` : "—",        trend: "", up: ouraData.hrv != null && ouraData.hrv >= 30, detail: ouraData.hrv != null ? `Current reading from ${src}` : "No data available" },
        { label: "Sleep",      value: ouraData.sleep_score != null ? `${Math.round(ouraData.sleep_score)}` : "—", trend: "", up: ouraData.sleep_score >= 70, detail: ouraData.sleep_score != null ? `Sleep score from ${src}` : "No data available" },
        // Lower RHR = good (was existence-only — any reading, even a
        // spike, rendered positive). No rolling mean is available in
        // this component to delta against (unlike DayDetail.jsx's
        // _fmtDelta), so this reuses the same >65bpm "Elevated"
        // threshold CheckIn.jsx already established for this metric
        // rather than inventing new numbers.
        { label: "Resting HR", value: ouraData.resting_hr != null ? `${Math.round(ouraData.resting_hr)} bpm` : "—", trend: "", up: ouraData.resting_hr != null && ouraData.resting_hr <= 65, detail: ouraData.resting_hr != null ? `Resting heart rate from ${src}` : "No data available" },
        { label: "Readiness",  value: ouraData.readiness != null ? `${Math.round(ouraData.readiness)}` : "—",  trend: "", up: ouraData.readiness >= 70, detail: ouraData.readiness != null ? `Readiness score from ${src}` : "No data available" },
      ];
    } else if (activeCode === 'BEH' && behMetrics) {
      metrics = behMetrics;
    }

    return {
      id: meta.id,
      label: meta.label,
      status: snapshot ? (STATUS_LABELS[snapshot.coherence_status] || "") : "",
      score,
      noDataToday,
      summary: buildSummary(domainWord, score, history),
      history,
      metrics,
      insights: snapshot ? buildInsights(snapshot.reason_codes, snapshot.window_end) : [],
    };
  })();

  if (loading) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="domain-detail" style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
          <span style={{ fontSize:13, color:P.textMute, fontWeight:300 }}>Loading...</span>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="domain-detail">

        {/* Ambient wash */}
        <div style={{
          position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 420, height: 340, pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 40% 0%, rgba(158,132,86,0.08) 0%, transparent 65%)",
        }} />

        <div style={{ position: "relative", zIndex: 1, paddingBottom: "var(--nav-clearance)" }}>

          {/* Header */}
          <div className="fu d1" style={{
            padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <button onClick={() => navigate('/dashboard')} style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              color: P.textMute, fontSize: 12, padding: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={P.textMute} strokeWidth="1.8" strokeLinecap="round">
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
              Dashboard
            </button>
            <Wordmark />
          </div>

          {/* Domain Tabs */}
          <div className="fu d1" style={{ margin: "24px 0 0" }}>
            <div style={{
              display: "flex", borderBottom: `1px solid ${P.border}`,
              padding: "0 24px",
            }}>
              {DOMAIN_ORDER.map((code) => {
                const d = DOMAIN_DATA[code];
                return (
                  <button key={code}
                    className={`domain-tab${activeCode === code ? " active" : ""}`}
                    onClick={() => setActiveCode(code)}>
                    <span style={{ fontSize: 9, letterSpacing: "0.14em",
                      textTransform: "uppercase", fontWeight: 500,
                      color: activeCode === code ? P.primary : P.textMute }}>
                      {d.id}
                    </span>
                    <span style={{ fontSize: 11,
                      fontWeight: activeCode === code ? 500 : 400,
                      color: activeCode === code ? P.text : P.textMute }}>
                      {DOMAIN_WORD[code] || code}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Domain Hero */}
          <div style={{ padding: "28px 24px 0" }} key={activeCode}>
            <div className="fu d1" style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", marginBottom: 20,
            }}>
              <div>
                <div style={{ fontSize: 9, color: P.primary, letterSpacing: "0.18em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 8 }}>
                  {D.id}{D.status ? <> &middot; {D.status}</> : null}
                </div>
                <h1 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 38, fontStyle: "italic", fontWeight: 400,
                  color: P.text, lineHeight: 1.08,
                }}>
                  {D.label}
                </h1>
              </div>

              {D.score != null && (
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <ScoreRing score={D.score} size={86} />
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ fontFamily: "'Playfair Display', serif",
                      fontSize: 22, fontStyle: "italic", color: P.text, lineHeight: 1 }}>
                      {D.score}
                    </span>
                    <span style={{ fontSize: 8, color: P.primaryMid,
                      letterSpacing: "0.12em", textTransform: "uppercase",
                      fontWeight: 500, marginTop: 2 }}>
                      score
                    </span>
                  </div>
                </div>
              )}
              {D.noDataToday && (
                <span style={{ fontSize: 11, color: P.textMute, letterSpacing: "0.12em",
                  textTransform: "uppercase", fontWeight: 500, flexShrink: 0 }}>
                  No data today
                </span>
              )}
            </div>

            {/* Summary */}
            {D.summary && (
              <div className="fu d2">
                <p style={{ fontSize: 14, color: P.textMid, lineHeight: 1.75,
                  fontWeight: 300, marginBottom: 28 }}>
                  {D.summary}
                </p>
              </div>
            )}

            {/* Honest empty state — no computed data yet. Shown instead of
                silent blankness so a brand-new user understands why the
                sections are missing. Never fabricated analysis. */}
            {!snapshot && (
            <div className="fu d2" style={{
              background: P.surface, borderRadius: 20,
              padding: "26px 22px",
              boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                textTransform: "uppercase", fontWeight: 500, marginBottom: 10 }}>
                Not enough data yet
              </div>
              <p style={{ fontSize: 13.5, color: P.textMid, lineHeight: 1.68,
                fontWeight: 300 }}>
                Check in daily and your {domainWord} signal will take shape here.
              </p>
            </div>
            )}

            {/* 7-Day Trend Chart */}
            {D.history.length >= 2 && (
            <div className="fu d2" style={{
              background: P.surface, borderRadius: 20,
              padding: "22px 16px 16px",
              boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
              marginBottom: 16,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 18, padding: "0 6px" }}>
                <span style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500 }}>
                  7-Day Trend
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%",
                    background: D.history[D.history.length-1].score > D.history[0].score
                      ? P.primary : P.textMute }} />
                  <span style={{ fontSize: 11, color: P.textMid }}>
                    {D.history[D.history.length-1].score > D.history[0].score
                      ? `+${D.history[D.history.length-1].score - D.history[0].score} this week`
                      : `${D.history[D.history.length-1].score - D.history[0].score} this week`}
                  </span>
                </div>
              </div>
              <TrendChart history={D.history} />
            </div>
            )}

            {/* Sub-Metrics */}
            {D.metrics && D.metrics.length > 0 && (
            <div className="fu d3" style={{
              background: P.surface, borderRadius: 20,
              padding: "20px 22px",
              boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                textTransform: "uppercase", fontWeight: 500, marginBottom: 4 }}>
                Key Signals
              </div>
              {D.metrics.map((m, i) => (
                <div key={i} className="metric-row">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: P.text, fontWeight: 400,
                      marginBottom: 3 }}>
                      {m.label}
                    </div>
                    <div style={{ fontSize: 11.5, color: P.textMute,
                      fontWeight: 300, lineHeight: 1.5 }}>
                      {m.detail}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 16 }}>
                    <div style={{ fontFamily: "'Playfair Display', serif",
                      fontSize: 15, fontStyle: "italic", color: P.text,
                      marginBottom: 3 }}>
                      {m.value}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 500,
                      color: m.up ? P.primary : P.textMute }}>
                      {m.trend}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* Check-in History */}
            {D.history.length > 0 && (
            <div className="fu d4" style={{
              background: P.surface, borderRadius: 20,
              padding: "20px 22px",
              boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                textTransform: "uppercase", fontWeight: 500, marginBottom: 16 }}>
                Check-in Log
              </div>
              {[...D.history].reverse().map((entry, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 14,
                  paddingBottom: i < D.history.length - 1 ? 12 : 0,
                  marginBottom: i < D.history.length - 1 ? 12 : 0,
                  borderBottom: i < D.history.length - 1 ? `1px solid ${P.border}` : "none",
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: P.primarySoft,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ fontFamily: "'Playfair Display', serif",
                      fontSize: 15, fontStyle: "italic", color: P.primary }}>
                      {entry.score}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: P.text,
                      marginBottom: 2 }}>
                      {entry.day}
                    </div>
                    <div style={{ fontSize: 11.5, color: P.textMid,
                      fontWeight: 300 }}>
                      {entry.note}
                    </div>
                  </div>
                  <div style={{ width: 48, height: 2, background: P.surfaceAlt,
                    borderRadius: 99, flexShrink: 0 }}>
                    <div style={{
                      height: "100%", width: `${entry.score}%`,
                      background: `linear-gradient(90deg, ${P.primary}, ${P.primaryMid})`,
                      borderRadius: 99,
                    }} />
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* Insights */}
            {D.insights.length > 0 && (
            <div className="fu d5" style={{
              background: P.surface, borderRadius: 20,
              padding: "20px 22px",
              boxShadow: "0 1px 16px rgba(28,24,20,0.07)",
              marginBottom: 16,
            }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500 }}>
                  Domain Insights
                </span>
              </div>
              {D.insights.map((item, i) => {
                const pill = INSIGHT_STYLES[item.type] || { bg: P.surfaceAlt, color: P.textMid };
                return (
                  <div key={i} className="insight-row">
                    <div style={{ display: "flex", alignItems: "center",
                      gap: 7, marginBottom: 7 }}>
                      <span className="pill"
                        style={{ background: pill.bg, color: pill.color }}>
                        {item.type}
                      </span>
                      <span style={{ fontSize: 10, color: P.textFaint }}>
                        {item.time}
                      </span>
                    </div>
                    <p style={{ fontSize: 13.5, lineHeight: 1.68,
                      color: P.text, fontWeight: 300 }}>
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
            )}

            {/* CTA — time-aware. The old copy was time-blind: "Start
                tonight's sleep protocol" rendered at 9am and routed to
                /protocol, which resolves to Morning Anchor at that hour.
                Evening-flavored copy now only renders in the evening/rest
                bands (and routes straight into the wind-down); during the
                day the copy names what /protocol actually opens. */}
            {(() => {
              const band = timeWindow();
              const eveningish = band === "evening" || band === null;
              // PSYCH's "Open today's reflection" goes to the actual
              // reflection surface (the check-in's journal), not the
              // protocol checklist; BIO/BEH route by band.
              const ctaHref = activeCode === "PSYCH"
                ? "/checkin"
                : eveningish ? "/evening" : "/protocol";
              return (
            <div className="fu d6" onClick={() => navigate(ctaHref)} style={{
              background: P.dark, borderRadius: 18,
              padding: "22px", display: "flex",
              alignItems: "center", justifyContent: "space-between",
              cursor: "pointer",
            }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: "0.14em",
                  textTransform: "uppercase", color: P.primaryMid,
                  fontWeight: 500, marginBottom: 6 }}>
                  Next step
                </div>
                <div style={{ fontFamily: "'Playfair Display', serif",
                  fontSize: 18, color: P.bg, fontStyle: "italic",
                  lineHeight: 1.35 }}>
                  {activeCode === "BIO" && (eveningish
                    ? <>Start tonight&rsquo;s<br />wind-down</>
                    : <>Open today&rsquo;s<br />protocol</>)}
                  {activeCode === "PSYCH" && <>Open today&rsquo;s<br />reflection</>}
                  {activeCode === "BEH" && (eveningish
                    ? <>Close the day<br />with intention</>
                    : <>Open today&rsquo;s<br />protocol</>)}
                </div>
              </div>
              <div style={{ width: 42, height: 42, borderRadius: "50%",
                background: P.primary, display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke={P.bg} strokeWidth="2" strokeLinecap="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </div>
              );
            })()}

          </div>
        </div>

        {/* Bottom Nav */}
        <BottomNav />

      </div>
    </>
  );
}
